import { format } from "date-fns";
import { CreatorAction } from "@/types/types";
import { useAuth } from "@clerk/react";
import {
  Frame,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@/components/ui/frame";
import { PaymentBlock } from "@/components/creators-table/phase/blocks";
import PayoutDecision from "@/components/creators-table/phase/PayoutDecision";
import { useDashboardStore, useEveActionsStore } from "@/stores";

/**
 * Amy-v2 payout-confirm task: a Grade payout is prepared. Confirm/cancel run
 * NATIVELY against the amy endpoints — the Clerk-authed click is the human
 * decision (the payout id itself never reaches the browser). Facts render
 * through PaymentBlock and the decision through PayoutDecision — the same
 * components the phase detail dialog uses, so both surfaces stay identical.
 */
export default function PayoutConfirmAction({ action }: { action: CreatorAction }) {
  const { getToken } = useAuth();
  const { confirmPayout, cancelPayout, isLoading } = useEveActionsStore();

  const preview = action.payout?.preview;
  const amount = preview?.amount != null ? `${preview.amount} ${preview.currency ?? ""}`.trim() : null;
  const expires = action.payout?.pending_expires_at
    ? format(new Date(action.payout.pending_expires_at), "MMM d, yyyy")
    : null;

  const removeTaskRow = () =>
    useDashboardStore.setState((state) => ({
      tasks: state.tasks.filter((task) => task._id !== action._id),
    }));

  const runConfirm = async () => {
    const token = await getToken();
    if (!token) return false;
    const ok = await confirmPayout(action._id, token);
    if (ok) removeTaskRow();
    return ok;
  };

  const runCancel = async (reason: string) => {
    const token = await getToken();
    if (!token) return false;
    const ok = await cancelPayout(action._id, reason, token);
    if (ok) removeTaskRow();
    return ok;
  };

  return (
    <div className="flex flex-col items-start gap-4 animate-in fade-in-0 slide-in-from-top-2 duration-250">
      <p className="text-sm text-muted-foreground">
        {action.message ?? `A payout for @${action.username} is prepared.`}
        {expires && ` The preview expires ${expires}.`}
      </p>

      {action.payout && (
        <Frame className="w-full">
          <FrameHeader>
            <FrameTitle>Payout</FrameTitle>
          </FrameHeader>
          <FramePanel className="p-0 overflow-hidden">
            <PaymentBlock payment={action.payout} />
          </FramePanel>
        </Frame>
      )}

      <Frame className="w-full">
        <FrameHeader>
          <FrameTitle>Decision</FrameTitle>
        </FrameHeader>
        <FramePanel className="flex w-full gap-2 p-3">
          <PayoutDecision
            username={action.username}
            avatar={action.avatar}
            amount={amount}
            isLoading={isLoading}
            onConfirm={runConfirm}
            onCancel={runCancel}
          />
        </FramePanel>
      </Frame>
    </div>
  );
}
